/**
 * The one rounding rule for fractional numbers in the UI (see Story 73).
 *
 * Every decimal a user reads — money, percentages, ratios — renders with
 * exactly two decimal places, so the same figure looks the same on the
 * dashboard, in analytics, and in a record's detail panel. Monetary amounts
 * go through CurrencyContext (format / formatInCurrency / formatDisplayAmount),
 * which applies the same two-decimal rule plus conversion and the symbol;
 * everything else comes through here.
 *
 * Integer counts are deliberately NOT covered by the two-decimal rule — "1,234
 * assets" must not become "1,234.00". Use formatCount() for those so they still
 * pick up locale grouping.
 */

export const DECIMAL_PLACES = 2;

/** Grouping locale matching CurrencyContext's: Indian digit grouping for INR. */
export const localeForCurrency = (code: string) => (code === 'INR' ? 'en-IN' : 'en-US');

const toNumber = (value: number | string | null | undefined): number => {
    const n = typeof value === 'number' ? value : Number(value ?? 0);
    return Number.isFinite(n) ? n : 0;
};

/**
 * A fractional number with locale grouping and exactly two decimals
 * (2.3 → "2.30", 1234.5 → "1,234.50"). Non-finite input reads as "0.00"
 * rather than "NaN".
 */
export function formatDecimal(
    value: number | string | null | undefined,
    locale?: string,
): string {
    return toNumber(value).toLocaleString(locale, {
        minimumFractionDigits: DECIMAL_PLACES,
        maximumFractionDigits: DECIMAL_PLACES,
    });
}

/** A percentage with its sign: 87.5 → "87.50%". Pass the value already in percent units. */
export function formatPercent(value: number | string | null | undefined): string {
    return `${formatDecimal(value)}%`;
}

/**
 * A whole-number count with locale grouping and no decimals (1234 → "1,234").
 * Counts are exempt from the two-decimal rule — this exists so they still
 * format consistently instead of calling toLocaleString() ad hoc.
 */
export function formatCount(value: number | string | null | undefined): string {
    return Math.round(toNumber(value)).toLocaleString(undefined, {
        maximumFractionDigits: 0,
    });
}
